<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { ref, PropType, useTemplateRef } from 'vue';
import type { IconName } from '@/ui/UiIcon.vue';

export interface DropdownOption {
  label: string;
  value: string;
  icon?: IconName;
  checked?: boolean;
  disabled?: boolean;
  noStroke?: boolean;
  id?: string;
}
export interface SelectedOptionId {
  optionName: string;
  id: string;
}

const props = defineProps({
  options: {
    type: Array as PropType<DropdownOption[]>,
    required: true,
  },
  triggerButtonLabel: {
    type: String,
    default: '',
  },
  hasCustomOptions: {
    type: Boolean,
    default: false,
  },
  hasCheckBox: {
    type: Boolean,
    default: false,
  },
  currentOption: {
    type: String,
    default: undefined,
  },
  multiselect: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: 'input', value: string): void;
}>();

const onInput = (option: DropdownOption) => {
  if (props.multiselect) {
    if (option) {
      option.checked = !option.checked;
      emit('input', option.value);
    }
  } else {
    props.options.forEach((filterOption: DropdownOption) => {
      filterOption.checked =
        option.value === filterOption.value ? !option.checked : false;
    });
    emit('input', option.value);
  }

  hide();
};

const isDropDownVisible = ref(false);
const hide = () => (isDropDownVisible.value = false);
const target = useTemplateRef<HTMLElement>('target');
const menuButton = useTemplateRef<HTMLElement>('menuButton');
const menuItems = useTemplateRef<HTMLElement>('menuItems');

const menuPosition = ref({ top: 0, left: 0 });
const inheritedFontSize = ref<string>('inherit');

onClickOutside(target, hide);

function updateMenuPosition() {
  if (!menuButton.value || !menuItems.value) {
    return;
  }

  const buttonRect = menuButton.value.getBoundingClientRect();
  const menuHeight = menuItems.value.offsetHeight;
  const menuWidth = menuItems.value.offsetWidth;
  const viewportHeight = window.innerHeight;
  const computedStyle = window.getComputedStyle(menuButton.value);
  inheritedFontSize.value = computedStyle.fontSize;

  // Check if menu would extend below viewport
  const spaceBelow = viewportHeight - buttonRect.bottom;
  const openAbove = spaceBelow < menuHeight && buttonRect.top > menuHeight;

  menuPosition.value = {
    top: openAbove ? buttonRect.top - menuHeight - 4 : buttonRect.bottom + 4,
    left: Math.max(8, buttonRect.right - menuWidth),
  };
}

// Close dropdown when scrolling occurs anywhere
function handleScroll() {
  if (isDropDownVisible.value) {
    hide();
  }
}

// Calculate position for teleported menu after it's rendered
watch(
  isDropDownVisible,
  async (visible) => {
    if (visible) {
      // Wait for menu to be rendered
      await nextTick();
      // Wait one more tick for dimensions to be calculated
      await nextTick();

      updateMenuPosition();

      // Add scroll listener to close on scroll
      window.addEventListener('scroll', handleScroll, true);
    } else {
      // Remove scroll listener when dropdown closes
      window.removeEventListener('scroll', handleScroll, true);
    }
  },
  { flush: 'post' },
);

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true);
});
</script>

<template>
  <div v-if="options.length || hasCustomOptions" ref="target" class="dropdown">
    <div class="menu">
      <div
        ref="menuButton"
        class="menuButton"
        @click.stop="isDropDownVisible = !isDropDownVisible"
      >
        <slot>
          <span v-if="triggerButtonLabel">
            {{ triggerButtonLabel }}
            <UiIcon name="ChevronDown" />
          </span>
          <span v-else>
            <UiIcon name="MoreVertical" />
          </span>
        </slot>
      </div>

      <Teleport to="body">
        <div
          v-if="isDropDownVisible"
          ref="menuItems"
          class="menuItems dropdown-teleported"
          :style="{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            fontSize: inheritedFontSize,
          }"
        >
          <div
            v-for="option in props.options"
            :key="option.value"
            class="menuItem"
          >
            <button
              :id="option.id"
              :disabled="option.disabled"
              class="dropdown-trigger"
              :class="{
                active: option.value === props.currentOption,
                logout: option.value === 'logout',
              }"
              @click.stop="
                () => {
                  onInput(option);
                }
              "
            >
              <UiIcon
                v-if="option.icon"
                class="icon icon-size-sm"
                :name="option.icon"
                :no-stroke="option.noStroke"
                :size="16"
              />
              <UiIcon
                v-else-if="option.value === currentOption"
                class="icon icon-size-sm"
                name="Check"
                :no-stroke="option.noStroke"
                :size="16"
              />
              <UiCheckbox
                v-if="hasCheckBox"
                :model-value="option.checked ?? false"
                :name="`${option.value}`"
              />
              <span>{{ option.label }} </span>
            </button>
          </div>

          <div class="menuItems-custom">
            <slot name="custom-options" />
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dropdown {
  width: fit-content;

  .menu {
    position: relative;
    text-align: left;
    display: inline-flex;
  }

  .menuButton {
    appearance: button;
    cursor: pointer;
    display: inline-flex;
    &:first-child {
      border-top-left-radius: var(--border-radius-14);
      border-top-right-radius: var(--border-radius-14);
    }
    &:last-child {
      border-bottom-right-radius: var(--border-radius-14);
      border-bottom-left-radius: var(--border-radius-14);
    }

    svg {
      width: px(24);
      height: px(24);
    }

    span {
      display: flex;
      flex-direction: row;
    }
  }

  &:first-child {
    border-top-left-radius: var(--border-radius-14);
    border-top-right-radius: var(--border-radius-14);
  }
  &:last-child {
    border-bottom-right-radius: var(--border-radius-14);
    border-bottom-left-radius: var(--border-radius-14);
  }
}
</style>

<style lang="scss">
// Teleported dropdown menu styles (rendered at body level)
.dropdown-teleported {
  background: white;
  color: var(--color-gray-700);
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  position: fixed;
  text-align: left;
  width: px(224);
  z-index: 9999;
  border-radius: var(--border-radius-8);
  overflow: hidden;

  .dropdown-trigger {
    cursor: pointer;
    background: white;
    display: flex;
    gap: 14px;
    padding: var(--space-8);
    color: var(--color-gray-700);
    width: 100%;
    border: 0;
    // The menu clips (overflow: hidden), so the ring stays inside the row.
    @include focus-ring($inset: true);

    &:disabled {
      cursor: default;
      color: var(--color-gray-400);
    }

    .icon {
      flex-shrink: 0;
    }
  }

  // The first and last row round their corners like the menu, so the inset
  // focus ring follows the corner instead of being cut by it.
  .menuItem:first-child .dropdown-trigger {
    border-top-left-radius: var(--border-radius-8);
    border-top-right-radius: var(--border-radius-8);
  }

  .menuItem:last-child .dropdown-trigger {
    border-bottom-left-radius: var(--border-radius-8);
    border-bottom-right-radius: var(--border-radius-8);
  }

  .menuItem,
  .menuItem span {
    text-align: left;
    & :hover {
      box-shadow: inset 0 0 4px var(--color-gray-100);
      background: var(--color-gray-50);
    }
  }

  .dropdown-trigger.active {
    background: var(--color-primary-50);
  }
  .logout {
    border-top: 1px solid var(--color-gray-200);
  }
}
</style>
